import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IppicaNazioneDto, IppicaQuotaTotDto } from 'types/swagger';
import { getQuota, getQuoteFinali } from './actions';
import {
  selectActiveScommessaForQuoteFinali,
  selectCavalliVincentiOrdineArrivoList,
  selectCavalloFactory,
  selectCodiceProgramma,
  selectCurrentScommesseType,
  selectNumeroAvvenimento,
  selectQuotaFromEsitoMap,
  selectTipoScommessQuotaFissaList,
} from './selectors';
import { IppicaLocale, TabValue } from './types';
import { isTotalizzatore } from './functions';
import {
  convertiNumeroCavalliMatrixInStringaEsadecimale,
  getCavalliFromCodiceTipoScommessa,
  isTipoScommessaAccoppiata,
  isTipoScommessaTrio,
} from './utils';
import { initIppicaQuoteFinali } from './ippicaSlice';
import { store } from 'sites/snai/lib/store';
import { convertAmountWithoutCurrencyValue } from 'utility/functions';

export type IppicaMenuProps = {
  type: TabValue;
};

export type IppicaNavHookType = {
  data: Array<IppicaNazioneDto>;
  isLoading: boolean;
};

export const useInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = useRef<() => void | null>(() => {});

  // Ricorda l'ultimo callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Imposta l'intervallo.
  useEffect(() => {
    const tick = () => {
      if (savedCallback?.current) {
        savedCallback.current();
      } else {
        return;
      }
    };
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    } else {
      return;
    }
  }, [delay]);
};

export const useGetCavallo = (key: string) => {
  const selectCavallo = useMemo(selectCavalloFactory, [key]);

  return useTypedSelector((st) => selectCavallo(st, key));
};

export const useGetQuoteFinali = () => {
  const dispatch = useAppDispatch();
  const tipoScommessaList = useTypedSelector(useMemo(() => selectActiveScommessaForQuoteFinali(false), []));
  const currentTypeScommessa = useTypedSelector(selectCurrentScommesseType);
  const codiceTipoScommessaList = useMemo(
    () => tipoScommessaList.map(({ codiceTipoScommessa }) => codiceTipoScommessa),
    [tipoScommessaList]
  );
  const scommesseQfList = useTypedSelector(selectTipoScommessQuotaFissaList);
  const cavalliVincentiOrdineArrivoList = useTypedSelector(selectCavalliVincentiOrdineArrivoList);
  const codiceProgramma = useTypedSelector(selectCodiceProgramma);
  const numeroAvvenimento = useTypedSelector(selectNumeroAvvenimento);

  useEffect(() => {
    const getData = async () => {
      await dispatch(getQuoteFinali({ codiceTipoScommessaList })).unwrap();
    };
    const getQuotaQf = async () => {
      const risultatoList: Array<IppicaQuotaTotDto> = [];
      for (const { codTipoScommessa } of scommesseQfList ?? []) {
        if (isTipoScommessaAccoppiata(codTipoScommessa) || isTipoScommessaTrio(codTipoScommessa)) {
          const primiCavalli = getCavalliFromCodiceTipoScommessa(codTipoScommessa, cavalliVincentiOrdineArrivoList);
          const combinazione = primiCavalli.join('-');
          const infoAggiuntivaByNumeroCavalliMatrix = convertiNumeroCavalliMatrixInStringaEsadecimale(
            [primiCavalli],
            codTipoScommessa
          );
          const esitoKey = `${codiceProgramma}_${numeroAvvenimento}_${codTipoScommessa}_${infoAggiuntivaByNumeroCavalliMatrix}_1`;
          let quota = selectQuotaFromEsitoMap(esitoKey)(store.getState());

          if (!quota) {
            quota = await dispatch(getQuota({ esitoKey })).unwrap();
          }

          const quotaFinaleItem: IppicaQuotaTotDto = {
            codiceTipoScommessa: codTipoScommessa,
            quota: quota ? (quota / 100).toString() : undefined,
            combinazione: combinazione,
            codiceProgramma,
            numeroAvvenimento,
            codiceStatoNumeroEvento: 0,
            numeroCavalliPartenti: 0,
            numeroEvento: 0,
          };
          risultatoList.push(quotaFinaleItem);
        }
      }
      dispatch(initIppicaQuoteFinali({ data: risultatoList, isLoading: false }));
    };

    if (isTotalizzatore(currentTypeScommessa!)) {
      if (codiceTipoScommessaList.length > 0) {
        getData();
      }
    } else {
      getQuotaQf();
    }
  }, [
    cavalliVincentiOrdineArrivoList,
    codiceProgramma,
    codiceTipoScommessaList,
    currentTypeScommessa,
    dispatch,
    numeroAvvenimento,
    scommesseQfList,
  ]);
};

export const useHasCampiActive = (tipoScommessaSelected: string | undefined) => {
  const hasCampi = useMemo(() => {
    return (
      tipoScommessaSelected &&
      tipoScommessaSelected !== '1' &&
      tipoScommessaSelected !== '2' &&
      tipoScommessaSelected !== '0-VIN'
    );
  }, [tipoScommessaSelected]);
  return hasCampi;
};
